// @ts-nocheck
/**
 * chat-flower-lib.js — Flower library browser for the chat widget.
 *
 * Right-side drawer with categorised flower/foliage/vessel thumbnails.
 * Users browse, select, and attach reference images that get sent to
 * Fleur alongside their message for arrangement design.
 *
 * Fetches manifest from the worker's GET /flower-library endpoint,
 * cached in localStorage with a 1-hour TTL.
 *
 * Exposes:
 *   window.AIFlorist.flowerLib.init(deps)
 *   window.AIFlorist.flowerLib.open()
 *   window.AIFlorist.flowerLib.close()
 *   window.AIFlorist.flowerLib.toggle()
 *   window.AIFlorist.flowerLib.getSelected()
 *   window.AIFlorist.flowerLib.clearSelected()
 *   window.AIFlorist.flowerLib.buildDrawerHTML()
 */
(function () {
  'use strict';

  window.AIFlorist = window.AIFlorist || {};
  var debug = window.AIFlorist.debug || { log: function () {}, warn: function () {}, error: function () {} };

  // ── Dependencies (set via init) ───────────────────────────────────
  var workerUrl = '';
  var onSelectionChange = function () {};

  // ── State ─────────────────────────────────────────────────────────
  var manifest = null;
  var selected = {};        // { key: { key, name, category } }
  var isOpen = false;
  var activeTab = 0;
  var expandedSections = {};
  var searchQuery = '';
  var uiLanguage = 'en';
  var DICT = {
    en: {
      notAvailable: 'Flower library is not available yet.',
      title: 'Flower Library',
      closeLibrary: 'Close library',
      searchPlaceholder: 'Search flowers...',
      loading: 'Loading...',
      noneSelected: 'None selected',
      done: 'Done',
      selected: 'selected',
      noFlowersYet: 'No flowers in library yet.',
      noMatches: 'No matches found.',
    },
    zh: {
      notAvailable: '花材库暂时不可用。',
      title: '花材库',
      closeLibrary: '关闭花材库',
      searchPlaceholder: '搜索花材...',
      loading: '加载中...',
      noneSelected: '未选择',
      done: '完成',
      selected: '已选',
      noFlowersYet: '花材库中还没有内容。',
      noMatches: '未找到匹配项。',
    },
    ja: {
      notAvailable: 'フラワーライブラリは現在利用できません。',
      title: 'フラワーライブラリ',
      closeLibrary: 'ライブラリを閉じる',
      searchPlaceholder: '花を検索...',
      loading: '読み込み中...',
      noneSelected: '未選択',
      done: '完了',
      selected: '選択済み',
      noFlowersYet: 'ライブラリに花材がまだありません。',
      noMatches: '一致する項目が見つかりません。',
    },
  };

  // ── DOM refs ──────────────────────────────────────────────────────
  var drawerEl = null;
  var overlayEl = null;
  var contentEl = null;
  var footerCountEl = null;
  var searchInputEl = null;

  // ── Cache ─────────────────────────────────────────────────────────
  var CACHE_KEY = 'afc_flower_lib';
  var CACHE_TTL = 3600000; // 1 hour

  function init(deps) {
    workerUrl = deps.workerUrl || '';
    onSelectionChange = deps.onSelectionChange || function () {};
    uiLanguage = detectLanguage();
  }

  // ── Manifest fetch with localStorage cache ────────────────────────

  function fetchManifest(callback) {
    if (manifest) { callback(manifest); return; }

    var cached = loadCache();
    if (cached) {
      manifest = cached;
      callback(manifest);
      return;
    }

    var xhr = new XMLHttpRequest();
    xhr.open('GET', workerUrl + '/flower-library');
    xhr.onload = function () {
      if (xhr.status === 200) {
        try {
          manifest = JSON.parse(xhr.responseText);
          saveCache(manifest);
          callback(manifest);
        } catch (e) {
          debug.error('[FlowerLib] Bad manifest JSON:', e);
          callback(null);
        }
      } else {
        debug.error('[FlowerLib] Manifest fetch failed:', xhr.status);
        callback(null);
      }
    };
    xhr.onerror = function () {
      debug.error('[FlowerLib] Manifest fetch error');
      callback(null);
    };
    xhr.send();
  }

  function loadCache() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (Date.now() - entry.ts > CACHE_TTL) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }
      return entry.data;
    } catch (e) { return null; }
  }

  function saveCache(data) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: data }));
    } catch (e) { /* quota exceeded — ignore */ }
  }

  // ── Drawer open / close ───────────────────────────────────────────

  function open() {
    if (isOpen) return;
    isOpen = true;

    if (!drawerEl) {
      buildDrawer();
    }

    fetchManifest(function (m) {
      if (m) renderContent();
      else if (contentEl) {
        contentEl.innerHTML = '<div class="afc-flower-empty">' + escapeHtml(t('notAvailable')) + '</div>';
      }
    });

    drawerEl.classList.add('afc-flower-drawer--open');
    overlayEl.classList.add('afc-flower-overlay--open');
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    if (drawerEl) drawerEl.classList.remove('afc-flower-drawer--open');
    if (overlayEl) overlayEl.classList.remove('afc-flower-overlay--open');
  }

  function toggle() {
    if (isOpen) close(); else open();
  }

  // ── Selection management ──────────────────────────────────────────

  function toggleSelect(item) {
    if (selected[item.key]) {
      delete selected[item.key];
    } else {
      if (Object.keys(selected).length >= 5) return;
      selected[item.key] = { key: item.key, name: item.name, category: item.category || '' };
    }
    updateSelectionUI();
    onSelectionChange(getSelected());
  }

  function getSelected() {
    var arr = [];
    for (var k in selected) {
      if (selected.hasOwnProperty(k)) arr.push(selected[k]);
    }
    return arr;
  }

  function clearSelected() {
    selected = {};
    updateSelectionUI();
    onSelectionChange([]);
  }

  function setSelected(items, silent) {
    selected = {};
    var next = items || [];
    for (var i = 0; i < next.length; i++) {
      if (!next[i] || !next[i].key) continue;
      selected[next[i].key] = {
        key: next[i].key,
        name: next[i].name || '',
        category: next[i].category || '',
      };
    }
    updateSelectionUI();
    if (!silent) onSelectionChange(getSelected());
  }

  function updateSelectionUI() {
    var count = Object.keys(selected).length;
    if (footerCountEl) {
      footerCountEl.textContent = count > 0 ? count + ' ' + t('selected') : t('noneSelected');
    }

    if (!contentEl) return;
    var cards = contentEl.querySelectorAll('.afc-flower-card');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var key = card.dataset.key;
      if (selected[key]) {
        card.classList.add('afc-flower-card--selected');
      } else {
        card.classList.remove('afc-flower-card--selected');
      }
    }
  }

  // ── Build drawer DOM ──────────────────────────────────────────────

  function buildDrawer() {
    var afcEl = document.querySelector('.afc');
    if (!afcEl) return;

    // Overlay (reuse pattern from history drawer)
    overlayEl = document.createElement('div');
    overlayEl.className = 'afc-flower-overlay';
    overlayEl.addEventListener('click', close);
    afcEl.appendChild(overlayEl);

    // Drawer
    drawerEl = document.createElement('div');
    drawerEl.className = 'afc-flower-drawer';
    drawerEl.innerHTML =
      '<div class="afc-flower-header">' +
        '<span class="afc-flower-title">' + escapeHtml(t('title')) + '</span>' +
        '<button class="afc-flower-close" aria-label="' + escapeHtml(t('closeLibrary')) + '">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
            '<line x1="18" y1="6" x2="6" y2="18"></line>' +
            '<line x1="6" y1="6" x2="18" y2="18"></line>' +
          '</svg>' +
        '</button>' +
      '</div>' +
      '<div class="afc-flower-search-wrap">' +
        '<input class="afc-flower-search" type="text" placeholder="' + escapeHtml(t('searchPlaceholder')) + '" />' +
      '</div>' +
      '<div class="afc-flower-tabs" id="afc-flower-tabs"></div>' +
      '<div class="afc-flower-content" id="afc-flower-content">' +
        '<div class="afc-flower-empty">' + escapeHtml(t('loading')) + '</div>' +
      '</div>' +
      '<div class="afc-flower-footer">' +
        '<span class="afc-flower-footer-count">' + escapeHtml(t('noneSelected')) + '</span>' +
        '<button class="afc-flower-done-btn">' + escapeHtml(t('done')) + '</button>' +
      '</div>';

    afcEl.appendChild(drawerEl);

    // Cache refs
    contentEl = drawerEl.querySelector('#afc-flower-content');
    footerCountEl = drawerEl.querySelector('.afc-flower-footer-count');
    searchInputEl = drawerEl.querySelector('.afc-flower-search');

    // Events
    drawerEl.querySelector('.afc-flower-close').addEventListener('click', close);
    drawerEl.querySelector('.afc-flower-done-btn').addEventListener('click', close);

    searchInputEl.addEventListener('input', function () {
      searchQuery = searchInputEl.value.trim().toLowerCase();
      renderContent();
    });
  }

  // ── Category ordering (Fresh Flowers first, rest alphabetical) ────

  var CATEGORY_ORDER = ['fresh', 'fillers', 'dried', 'vessels', 'wraps', 'structures'];

  function reorderCategories(cats) {
    var ordered = [];
    for (var i = 0; i < CATEGORY_ORDER.length; i++) {
      for (var j = 0; j < cats.length; j++) {
        if (cats[j].id === CATEGORY_ORDER[i]) { ordered.push(cats[j]); break; }
      }
    }
    for (var k = 0; k < cats.length; k++) {
      if (CATEGORY_ORDER.indexOf(cats[k].id) === -1) ordered.push(cats[k]);
    }
    return ordered;
  }

  // ── Render manifest content ───────────────────────────────────────

  function renderContent() {
    if (!manifest || !contentEl) return;

    var cats = reorderCategories(manifest.categories || []);
    if (cats.length === 0) {
      contentEl.innerHTML = '<div class="afc-flower-empty">' + escapeHtml(t('noFlowersYet')) + '</div>';
      return;
    }

    // Render tabs
    var tabsEl = drawerEl.querySelector('#afc-flower-tabs');
    if (tabsEl) {
      var tabsHtml = '';
      for (var t = 0; t < cats.length; t++) {
        var activeClass = t === activeTab ? ' afc-flower-tab--active' : '';
        tabsHtml += '<button class="afc-flower-tab' + activeClass + '" data-tab="' + t + '">' +
          escapeHtml(cats[t].name) + '</button>';
      }
      tabsEl.innerHTML = tabsHtml;

      var tabBtns = tabsEl.querySelectorAll('.afc-flower-tab');
      for (var tb = 0; tb < tabBtns.length; tb++) {
        tabBtns[tb].addEventListener('click', handleTabClick);
      }
    }

    // Render active category
    if (activeTab >= cats.length) activeTab = 0;
    var cat = cats[activeTab];
    var html = '';

    var subs = cat.subcategories || [];
    var hasResults = false;

    for (var s = 0; s < subs.length; s++) {
      var sub = subs[s];
      var filteredItems = filterItems(sub.items);
      if (filteredItems.length === 0) continue;
      hasResults = true;

      var sectionKey = cat.id + '/' + sub.id;
      var isExpanded = expandedSections[sectionKey] !== false;

      html += '<div class="afc-flower-section" data-section="' + sectionKey + '">' +
        '<button class="afc-flower-section-header' + (isExpanded ? ' afc-flower-section-header--open' : '') + '">' +
          '<span>' + escapeHtml(sub.name) + ' (' + filteredItems.length + ')</span>' +
          '<svg class="afc-flower-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
            '<polyline points="6 9 12 15 18 9"></polyline>' +
          '</svg>' +
        '</button>';

      if (isExpanded) {
        html += '<div class="afc-flower-grid">';
        for (var i = 0; i < filteredItems.length; i++) {
          var item = filteredItems[i];
          var isSelected = !!selected[item.key];
          var thumbUrl = workerUrl + '/images/' + item.key;

          html += '<div class="afc-flower-card' + (isSelected ? ' afc-flower-card--selected' : '') + '" data-key="' + escapeHtml(item.key) + '" data-name="' + escapeHtml(item.name) + '">' +
            '<div class="afc-flower-thumb-wrap">' +
              '<img class="afc-flower-thumb" src="' + thumbUrl + '" alt="' + escapeHtml(item.name) + '" loading="lazy" />' +
              '<div class="afc-flower-check">' +
                '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">' +
                  '<polyline points="20 6 9 17 4 12"></polyline>' +
                '</svg>' +
              '</div>' +
            '</div>' +
            '<span class="afc-flower-name">' + escapeHtml(item.name) + '</span>' +
          '</div>';
        }
        html += '</div>';
      }
      html += '</div>';
    }

    if (!hasResults) {
      html = '<div class="afc-flower-empty">' + escapeHtml(t('noMatches')) + '</div>';
    }

    contentEl.innerHTML = html;

    // Bind section toggle events
    var headers = contentEl.querySelectorAll('.afc-flower-section-header');
    for (var h = 0; h < headers.length; h++) {
      headers[h].addEventListener('click', handleSectionToggle);
    }

    // Bind card click events
    var cards = contentEl.querySelectorAll('.afc-flower-card');
    for (var c = 0; c < cards.length; c++) {
      cards[c].addEventListener('click', handleCardClick);
    }
  }

  function filterItems(items) {
    if (!searchQuery) return items;
    var results = [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].name.toLowerCase().indexOf(searchQuery) !== -1) {
        results.push(items[i]);
      }
    }
    return results;
  }

  // ── Event handlers ────────────────────────────────────────────────

  function handleTabClick(e) {
    var btn = e.currentTarget;
    activeTab = parseInt(btn.dataset.tab, 10);
    expandedSections = {};
    renderContent();
  }

  function handleSectionToggle(e) {
    var section = e.currentTarget.closest('.afc-flower-section');
    if (!section) return;
    var key = section.dataset.section;
    var wasExpanded = expandedSections[key] !== false;
    expandedSections[key] = !wasExpanded;
    renderContent();
  }

  function handleCardClick(e) {
    var card = e.currentTarget;
    var key = card.dataset.key;
    var name = card.dataset.name;
    if (!key) return;

    toggleSelect({ key: key, name: name });
  }

  // ── Utility ───────────────────────────────────────────────────────

  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function detectLanguage() {
    var root = document.getElementById('aiflorist-chat-root');
    var raw =
      (root && root.dataset && (root.dataset.language || root.dataset.locale)) ||
      (document.documentElement && document.documentElement.lang) ||
      '';
    var base = String(raw).trim().toLowerCase().replace('_', '-').split('-')[0];
    if (base === 'zh' || base === 'ja') return base;
    return 'en';
  }

  function t(key) {
    var dict = DICT[uiLanguage] || DICT.en;
    return dict[key] || DICT.en[key] || key;
  }

  function buildDrawerHTML() {
    return '';
  }

  // ── Public API ────────────────────────────────────────────────────

  window.AIFlorist.flowerLib = {
    init: init,
    open: open,
    close: close,
    toggle: toggle,
    getSelected: getSelected,
    clearSelected: clearSelected,
    setSelected: setSelected,
    buildDrawerHTML: buildDrawerHTML,
  };
})();
